/**
 * Индикатор 24h окна для WhatsApp WABA (TODO-027a)
 * Дата: 15.01.2026
 *
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - `frontend/src/api/waba.ts` — endpoint window-status
 * - `frontend/src/components/features/messages/ChatWindow.tsx` — показывает индикатор для WABA
 *
 * КАК РАБОТАЕТ (кратко):
 * - Запрашивает `/api/conversations/window-status` и показывает open/closed + expiry
 */
import { useMemo } from 'react';
import type { WABAWindowStatusResponse } from '@/api/waba';
import { cn } from '@/utils/cn';

interface WindowStatusIndicatorProps {
  enabled?: boolean;
  isLoading?: boolean;
  data?: WABAWindowStatusResponse | null;
}

function formatDurationMs(ms: number) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return h > 0 ? `${h}ч ${m}м` : `${m}м`;
}

export function WindowStatusIndicator({ enabled = true, isLoading = false, data: dataProp = null }: WindowStatusIndicatorProps) {
  const view = useMemo(() => {
    const data = dataProp;
    if (!data) return null;
    if (!data.window_open) {
      return { state: 'closed' as const };
    }
    if (!data.window_expires_at) {
      return { state: 'open' as const, label: 'Окно открыто' };
    }
    const expiresAt = new Date(data.window_expires_at).getTime();
    const remaining = expiresAt - Date.now();
    return { state: 'open' as const, label: `Окно открыто (осталось ${formatDurationMs(remaining)})` };
  }, [dataProp]);

  if (!enabled) return null;

  if (isLoading) {
    return (
      <div className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs text-neutral-500">
        Проверяем 24h окно…
      </div>
    );
  }

  if (!view) return null;

  if (view.state === 'closed') {
    return (
      <div className={cn('rounded-md border px-3 py-2 text-xs', 'border-red-200 bg-red-50 text-red-800')} data-testid="waba-window-closed">
        <div className="font-medium">Окно закрыто</div>
        <div className="text-[11px] mt-0.5">Для начала диалога отправьте шаблон WABA (нажмите /)</div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-md border px-3 py-2 text-xs', 'border-green-200 bg-green-50 text-green-800')} data-testid="waba-window-open">
      <div className="font-medium">{view.label}</div>
      <div className="text-[11px] mt-0.5">Можно отправлять обычные сообщения</div>
    </div>
  );
}

